import { StandardEvents } from '@shopify/events';

/**
 * Añade el total al botón de compra: «Añadir al carrito · 72,90 €».
 *
 * El precio de la variante llega del HTML que ya re-renderiza el tema en cada
 * cambio, así que aquí solo se multiplica por la cantidad y se suman los
 * extras marcados (el envoltorio). No sustituye el texto del botón: lo añade
 * en un elemento aparte, para no pisar los estados de «Agotado» o «Añadido».
 */
class MrBuyTotal extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section');

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    section?.addEventListener('mr:extras-changed', this.#render, { signal });
    document.addEventListener('change', this.#handleChange, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#target?.querySelector('.mr-buy-total')?.remove();
  }

  /**
   * El botón de compra principal. La barra fija tiene su propio botón con la
   * misma clase y va antes en la página: sin excluirlo, el total se pegaba ahí,
   * donde no entra y repite el precio que la barra ya muestra al lado.
   */
  get #target() {
    const section = this.closest('.shopify-section');
    const candidates = section?.querySelectorAll('.add-to-cart-text, [ref="addToCartButton"]') ?? [];
    return [...candidates].find((element) => !element.closest('sticky-add-to-cart')) ?? null;
  }

  get #quantity() {
    const input = this.closest('.shopify-section')?.querySelector('input[name="quantity"]');
    return input instanceof HTMLInputElement ? Math.max(1, Number(input.value) || 1) : 1;
  }

  /** Extras marcados en la ficha, como el envoltorio de regalo. */
  get #extras() {
    let total = 0;

    for (const wrap of document.querySelectorAll('mr-gift-wrap')) {
      const toggle = wrap.querySelector('[data-mr-wrap-toggle]');
      const price = Number(wrap.getAttribute('data-variant-price')) || 0;
      if (toggle instanceof HTMLInputElement && toggle.checked) total += price;
    }

    return total;
  }

  /**
   * Grabado como servicio aparte (modo servicio de mr-engraving): se cobra por
   * unidad, así que va con el precio de la variante.
   */
  get #service() {
    let total = 0;
    for (const engraving of this.closest('.shopify-section')?.querySelectorAll('mr-engraving[data-mr-service="true"]') ?? []) {
      if (engraving instanceof HTMLElement) total += Number(engraving.dataset.mrServicePrice) || 0;
    }
    return total;
  }

  #render = () => {
    const target = this.#target;
    if (!(target instanceof HTMLElement)) return;

    const price = Number(this.dataset.variantPrice) || 0;
    if (!price) return;

    const total = (price + this.#service) * this.#quantity + this.#extras;

    let slot = target.querySelector('.mr-buy-total');
    if (!slot) {
      slot = document.createElement('span');
      slot.className = 'mr-buy-total';
      target.appendChild(slot);
    }

    slot.textContent = ` · ${this.#money(total)}`;
  };

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat ?? '{{amount}}';
    const withComma = format.includes('amount_with_comma_separator');
    const number = (cents / 100).toLocaleString(withComma ? 'es-ES' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return format.replace(/\{\{\s*amount[^}]*\}\}/, number);
  }

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name === 'quantity' || target.matches('[data-mr-wrap-toggle]')) this.#render();
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector('mr-buy-total');
        if (fresh instanceof HTMLElement) this.dataset.variantPrice = fresh.dataset.variantPrice ?? '';
        this.#render();
      })
      .catch(() => {
        /* si falla, se queda el total anterior */
      });
  };
}

if (!customElements.get('mr-buy-total')) {
  customElements.define('mr-buy-total', MrBuyTotal);
}
